import { type FilterValue } from "@shopify/hydrogen-react/storefront-api-types";

import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import {
  type AdjacentCollectionsConnection,
  CollectionContentCardType,
  type CollectionContentCard,
  type CollectionContentCardConnection,
} from "@root/tablescape/state";
import { formatMoney } from "@root/tablescape/utilities";

export const generatePriceFilterValue = (
  min: number,
  max: number
): FilterValue => ({
  id: COLLECTION_FILTER_IDS.PRICE,
  label: `${formatMoney(
    min * 100,
    // eslint-disable-next-line no-template-curly-in-string
    "${{ amount_no_trailing_zeroes }}",
    false
  )} - ${formatMoney(
    max * 100,
    // eslint-disable-next-line no-template-curly-in-string
    "${{ amount_no_trailing_zeroes }}",
    false
  )}`,
  count: 0,
  input: JSON.stringify({
    price: {
      min,
      max,
    },
  }),
});

export const formatCollectionContentCard = (
  contentCardConnection: CollectionContentCardConnection
) => ({
  background: contentCardConnection.background?.value,
  ctaText: contentCardConnection.ctaText?.value,
  ctaUrl: contentCardConnection.ctaUrl?.value,
  description: contentCardConnection.description?.value,
  image: contentCardConnection.image?.reference,
  mobileImage: contentCardConnection.mobileImage?.reference,
  title: contentCardConnection.title?.value,
  videoControlColor: contentCardConnection.videoControlColor?.value,
  videoMp4: contentCardConnection.videoMp4?.reference,
  videoWebm: contentCardConnection.videoWebm?.reference,
});

export const formatCollectionContentCards = (
  nodes: {
    contentCard: { reference: CollectionContentCardConnection };
    id: string;
    position: { value: string };
  }[]
) =>
  nodes.map((node) => ({
    contentCard: formatCollectionContentCard(node.contentCard.reference),
    id: node.id,
    position: node.position.value,
  }));

export const formatAdjacentCollections = (
  adjacentCollections?: AdjacentCollectionsConnection
) =>
  adjacentCollections?.references.nodes.map(
    ({ title, titleOverride, ...rest }) => ({
      title: titleOverride?.value ?? title,
      ...rest,
    })
  ) ?? [];

export const getCollectionContentCardType = ({
  description,
  image,
  title,
  videoMp4,
  videoWebm,
}: CollectionContentCard) => {
  if (videoMp4 || videoWebm) {
    return CollectionContentCardType.VIDEO_CARD;
  } else if (image && !(title && description)) {
    return CollectionContentCardType.IMAGE_CARD;
  } else if (image && (title || description)) {
    return CollectionContentCardType.IMAGE_TEXT_CARD;
  } else {
    return CollectionContentCardType.TEXT_CARD;
  }
};
